import React, { useMemo, useState } from 'react';
import useAPI from '../../hooks/useAPI';
import usePostProgram from '../../hooks/usePostProgram';
import usePutProgram from '../../hooks/useUpdateProgram';
import useDeleteProgram from '../../hooks/useDeleteProgram';
import endpointsAdmin from '../../Services/endpointAdmin';
import { resolveProgramFundingMeta } from '../../Services/penyaluranFundingMap';

function extractArrayPayload(payload, preferredKeys = []) {
  if (Array.isArray(payload)) return payload;

  for (const key of preferredKeys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }

  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.result)) return payload.result;

  return [];
}

const initialForm = {
  id_divisi: '',
  nama_program: '',
};

const requiredFieldProps = (message) => ({
  required: true,
  title: message,
  onInvalid: (event) => event.target.setCustomValidity(message),
  onInput: (event) => event.target.setCustomValidity(''),
});

const inputClass = 'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs sm:text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500';

function PenerimaManfaatAdmin() {
  const { data, loading, error, refetch } = useAPI(endpointsAdmin.program.getAll);
  const { data: divisiData } = useAPI(endpointsAdmin.divisi.getAll);
  const { postProgram, loading: creating, error: createError } = usePostProgram();
  const { putProgram, loading: updating, error: updateError } = usePutProgram();
  const { deleteProgram, loading: deleting, error: deleteError } = useDeleteProgram();

  const [formData, setFormData] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [activeDeleteId, setActiveDeleteId] = useState(null);

  const programList = useMemo(() => extractArrayPayload(data, ['program']), [data]);
  const divisiList = useMemo(() => {
    const directDivisi = extractArrayPayload(divisiData, ['divisi']);
    if (directDivisi.length > 0) return directDivisi;

    const uniqueDivisi = new Map();
    programList.forEach((item) => {
      const idDivisi = String(item?.id_divisi || item?.divisi?.id_divisi || item?.divisi?.id || '').trim();
      const namaDivisi = String(item?.divisi?.nama_divisi || item?.nama_divisi || '').trim();
      if (idDivisi && namaDivisi && !uniqueDivisi.has(idDivisi)) {
        uniqueDivisi.set(idDivisi, {
          id_divisi: idDivisi,
          nama_divisi: namaDivisi,
        });
      }
    });

    return Array.from(uniqueDivisi.values());
  }, [divisiData, programList]);

  const filteredPrograms = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    if (!keyword) return programList;
    return programList.filter((item) =>
      [item.id_program, item.id_divisi, item.nama_program, item.divisi?.nama_divisi]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword))
    );
  }, [programList, searchTerm]);

  const programFundingMap = useMemo(() => {
    const map = new Map();
    filteredPrograms.forEach((item) => {
      map.set(String(item.id_program), resolveProgramFundingMeta(item));
    });
    return map;
  }, [filteredPrograms]);

  const currentError = error || createError || updateError || deleteError;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData(initialForm);
    setEditingId(null);
  };

  const handleEdit = (item) => {
    setEditingId(item.id_program);
    setActionMessage('');
    setFormData({
      id_divisi: item.id_divisi ? String(item.id_divisi) : '',
      nama_program: item.nama_program || '',
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setActionMessage('');

    if (!formData.id_divisi || !formData.nama_program.trim()) {
      setActionMessage('ID divisi dan nama program wajib diisi.');
      return;
    }

    const result = editingId
      ? await putProgram(editingId, formData.id_divisi, formData.nama_program.trim())
      : await postProgram(formData.id_divisi, formData.nama_program.trim());

    if (result) {
      setActionMessage(editingId ? 'Program berhasil diperbarui.' : 'Program berhasil ditambahkan.');
      resetForm();
      await refetch();
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Hapus program "${item.nama_program}"?`)) return;

    setActionMessage('');
    setActiveDeleteId(item.id_program);
    const result = await deleteProgram(item.id_program);

    if (result) {
      setActionMessage('Program berhasil dihapus.');
      if (editingId === item.id_program) resetForm();
      await refetch();
    }

    setActiveDeleteId(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6 py-12">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600" />
        <p className="mt-4 text-sm font-semibold text-slate-700">Memuat data program...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/70 px-4 sm:px-6 lg:px-12 py-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Page Header */}
        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-900 via-blue-900 to-blue-950 text-white shadow-[0_20px_50px_rgba(15,23,42,0.15)]">
          <div className="px-8 py-10 lg:py-12">
            <div className="inline-block rounded-full bg-white/10 backdrop-blur px-4 py-2 text-xs font-semibold uppercase tracking-wider text-blue-200 mb-4">
              Manajemen Program
            </div>
            <h1 className="text-3xl font-bold leading-tight lg:text-4xl">
              Program Penyaluran
            </h1>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-200">
              Kelola daftar program penyaluran yang tersedia. Tambah, ubah, atau hapus program sesuai kebutuhan divisi.
            </p>
          </div>
        </section>

        {/* Feedback Alert */}
        {(actionMessage || currentError) && (
          <div className={`rounded-xl border p-4 text-xs sm:text-sm font-semibold ${
            currentError ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'
          }`}>
            {currentError || actionMessage}
          </div>
        )}

        {/* Main Grid: Form + Table */}
        <div className="grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-6">
          {/* LEFT: Form Card */}
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden self-start">
            <div className="p-5 border-b border-slate-100">
              <h2 className="text-base font-bold text-slate-900">
                {editingId ? 'Ubah Program' : 'Tambah Program Baru'}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">Divisi dan nama program wajib diisi.</p>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Divisi <span className="text-red-500">*</span>
                </label>
                <select
                  name="id_divisi"
                  value={formData.id_divisi}
                  onChange={handleChange}
                  {...requiredFieldProps('Divisi wajib dipilih.')}
                  className={inputClass}
                >
                  <option value="">-- Pilih Divisi --</option>
                  {divisiList.map((item) => (
                    <option key={item.id_divisi} value={item.id_divisi}>{item.nama_divisi}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
                  Nama Program <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nama_program"
                  value={formData.nama_program}
                  onChange={handleChange}
                  placeholder="Contoh: Beasiswa Yatim, Pembangunan Masjid..."
                  {...requiredFieldProps('Nama program wajib diisi.')}
                  className={inputClass}
                />
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="submit"
                  disabled={creating || updating}
                  className="flex-1 inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-bold text-white shadow-sm hover:bg-blue-700 disabled:opacity-70 transition"
                >
                  {creating || updating ? 'Menyimpan...' : editingId ? 'Simpan Perubahan' : 'Tambah Program'}
                </button>
                {editingId && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-bold text-slate-700 hover:bg-slate-100 transition"
                  >
                    Batal
                  </button>
                )}
              </div>
            </form>
          </section>

          {/* RIGHT: Program Table */}
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-base font-bold text-slate-900">Daftar Program</h2>
                <p className="text-xs text-slate-500">{filteredPrograms.length} program ditemukan</p>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Cari program..."
                className="w-full sm:w-60 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs sm:text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">ID</th>
                    <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Divisi</th>
                    <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Nama Program</th>
                    <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-600">Sumber Dana</th>
                    <th className="px-5 py-3 text-center text-[11px] font-bold uppercase tracking-wider text-slate-600">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredPrograms.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-5 py-10 text-center text-xs text-slate-400">
                        Belum ada program penyaluran.
                      </td>
                    </tr>
                  ) : filteredPrograms.map((item) => {
                    const fundingMeta = programFundingMap.get(String(item.id_program));
                    const isMapped = Boolean(fundingMeta?.hasFundingRule);

                    return (
                      <tr key={item.id_program} className="hover:bg-slate-50/80 transition">
                        <td className="px-5 py-3 text-slate-700 font-mono text-[11px]">{item.id_program}</td>
                        <td className="px-5 py-3">
                          <p className="font-semibold text-slate-800">{item.divisi?.nama_divisi || 'Divisi belum terhubung'}</p>
                          <p className="text-[11px] text-slate-500">ID: {item.id_divisi || '-'}</p>
                        </td>
                        <td className="px-5 py-3 font-semibold text-slate-900">{item.nama_program || '-'}</td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                            fundingMeta?.sourceType === 'zakat'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : fundingMeta?.sourceType === 'infak'
                                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                : fundingMeta?.sourceType === 'wakaf'
                                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                  : 'bg-slate-100 text-slate-600 border border-slate-200'
                          }`}>
                            {isMapped ? fundingMeta.sourceLabel : 'Belum dipetakan'}
                          </span>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleEdit(item)}
                              className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-bold text-blue-700 hover:bg-blue-100 transition"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(item)}
                              disabled={deleting && activeDeleteId === item.id_program}
                              className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-[11px] font-bold text-red-700 hover:bg-red-100 disabled:opacity-50 transition"
                            >
                              {deleting && activeDeleteId === item.id_program ? 'Hapus...' : 'Hapus'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default PenerimaManfaatAdmin;
